export interface SafeStringifyOptions {
  /** Number of spaces for indentation. Use 0 or undefined for compact output */
  indent?: number
  /** Replacement text for circular references */
  circularReplacement?: string
}

/**
 * Safely stringifies an object to JSON, handling circular references and errors.
 *
 * @param obj - The object to stringify
 * @param options - Configuration options
 * @returns JSON string representation or error message
 *
 * @example
 * const obj = { name: 'John' }
 * obj.self = obj
 * safeStringify(obj) // Returns valid JSON with circular reference handled
 *
 * @example
 * safeStringify(data, { indent: 4, circularReplacement: '[CIRCULAR]' })
 */
export function safeStringify(obj: unknown, options: SafeStringifyOptions = {}): string {
  const { indent = 2, circularReplacement = '[Circular Reference]' } = options

  const seen = new Set<unknown>()

  try {
    return JSON.stringify(
      obj,
      (_key: string, value: unknown) => {
        // Handle BigInt - JSON.stringify doesn't support it natively
        if (typeof value === 'bigint') {
          return `${value.toString()}n`
        }

        // Handle circular references
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return circularReplacement
          }
          seen.add(value)
        }

        return value
      },
      indent,
    )
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    return `[Error stringifying object: ${errorMessage}]`
  }
}
